import autoBind from 'auto-bind';
import * as O from 'fp-ts/lib/Option';
import { CancelFilterPayload } from '~/core/state/actions';
import { BooleanFilter } from './BooleanFilter';
import { Filter } from './Filter';
import { KeyWordFilter } from './KeyWordFilter';
import { NumberFilter } from './NumberFilter';
import { UpdateBooleanFilterPayload_ControlsState } from './types/booleanFilter.type';
import {
    Filter_Config,
    Filter_ControlsState,
    Filter_RequestParams,
    Filter_Snapshot,
    Filter_State,
    FilterAggregation,
    UpdateFilterPayload_ControlsState,
} from './types/filters.type';
import { UpdateKeyWordsFilterPayload_ControlsState } from './types/keywordFilter.type';
import {
    isNumberRangeFilterAsKeywordsConfig,
    UpdateNumberFilterPayload_ControlsState,
} from './types/numberFilter.type';

export class GeneralFilter implements Filter {
    private keyWordFilter: KeyWordFilter;
    private numberFilter: NumberFilter;
    private booleanFilter: BooleanFilter;

    constructor() {
        this.keyWordFilter = new KeyWordFilter();
        this.numberFilter = new NumberFilter();
        this.booleanFilter = new BooleanFilter();

        autoBind(this);
    }

    public createStateFromConfig(config: Filter_Config): Filter_State {
        if (this.keyWordFilter.isKeywordFilterConfig(config)) {
            return this.keyWordFilter.createStateFromConfig(config);
        }

        if (this.numberFilter.isNumberFilterConfig(config) && isNumberRangeFilterAsKeywordsConfig(config)) {
            return this.keyWordFilter.createStateFromNumberConfig(config);
        }

        if (this.numberFilter.isNumberFilterConfig(config)) {
            return this.numberFilter.createStateFromConfig(config);
        }

        if (this.booleanFilter.isBooleanFilterConfig(config)) {
            return this.booleanFilter.createStateFromConfig(config);
        }

        throw new Error('Unknown filter type in Filter->createStateFromConfig');
    }

    public createControlsStateFromConfig(config: Filter_Config): Filter_ControlsState {
        if (this.keyWordFilter.isKeywordFilterConfig(config)) {
            return this.keyWordFilter.createControlsStateFromConfig(config);
        }

        if (
            this.numberFilter.isNumberFilterConfig(config) &&
            config.configuration.subtype === 'explicitValues' &&
            config.values.keywords?.length &&
            config.values.keywords.length > 0
        ) {
            return this.keyWordFilter.createControlsStateFromNumberConfig(config);
        }

        if (this.numberFilter.isNumberFilterConfig(config)) {
            return this.numberFilter.createControlsStateFromConfig(config);
        }

        if (this.booleanFilter.isBooleanFilterConfig(config)) {
            return this.booleanFilter.createControlsStateFromConfig(config);
        }

        throw new Error('Unknown filter type in Filter->createControlsStateFromConfig');
    }

    public updateControlsState(
        controlsState: Filter_ControlsState,
        state: Filter_State,
        payload: UpdateFilterPayload_ControlsState
    ): Filter_ControlsState {
        if (
            this.keyWordFilter.isKeywordFilterControlsState(controlsState) &&
            this.keyWordFilter.isKeywordFilterState(state)
        ) {
            return this.keyWordFilter.updateControlsState(
                controlsState,
                state,
                payload as UpdateKeyWordsFilterPayload_ControlsState
            ); // TODO: add proper type guards for payload
        }

        if (
            this.numberFilter.isNumberFilterControlsState(controlsState) &&
            this.numberFilter.isNumberFilterState(state)
        ) {
            return this.numberFilter.updateControlsState(
                controlsState,
                state,
                payload as UpdateNumberFilterPayload_ControlsState
            ); // TODO: add proper type guards for payload
        }

        if (
            this.booleanFilter.isBooleanFilterControlsState(controlsState) &&
            this.booleanFilter.isBooleanFilterState(state)
        ) {
            return this.booleanFilter.updateControlsState(
                controlsState,
                state,
                payload as UpdateBooleanFilterPayload_ControlsState
            ); // TODO: add proper type guards for payload
        }

        return controlsState;
    }

    public resetControlsState(controlsState: Filter_ControlsState): Filter_ControlsState {
        if (this.keyWordFilter.isKeywordFilterControlsState(controlsState)) {
            return this.keyWordFilter.resetControlsState(controlsState);
        }
        return controlsState;
    }

    public applyAggregation(
        state: Filter_State,
        controlsState: Filter_ControlsState,
        aggregation: FilterAggregation
    ): Filter_State {
        if (
            this.booleanFilter.isBooleanFilterState(state) &&
            this.booleanFilter.isBooleanFilterControlsState(controlsState) &&
            this.booleanFilter.isBooleanFilterAggregation(aggregation)
        ) {
            return this.booleanFilter.applyAggregation(state, controlsState, aggregation);
        }

        if (
            this.keyWordFilter.isKeywordFilterState(state) &&
            this.keyWordFilter.isKeywordFilterControlsState(controlsState) &&
            this.keyWordFilter.isKeywordFilterAggregation(aggregation)
        ) {
            return this.keyWordFilter.applyAggregation(state, controlsState, aggregation);
        }

        if (
            this.numberFilter.isNumberFilterState(state) &&
            this.numberFilter.isNumberFilterControlsState(controlsState) &&
            this.numberFilter.isNumberFilterAggregation(aggregation)
        ) {
            return this.numberFilter.applyAggregation(state, controlsState, aggregation);
        }

        return state;
    }

    public cancelFilter(
        controlsState: Filter_ControlsState,
        payload: Pick<CancelFilterPayload, 'cancelAll' | 'key'>,
        config?: Filter_Config
    ): Filter_ControlsState {
        if (this.keyWordFilter.isKeywordFilterControlsState(controlsState) && payload?.key) {
            return this.keyWordFilter.cancelFilter(controlsState, { key: payload.key });
        }

        if (this.keyWordFilter.isKeywordFilterControlsState(controlsState) && payload.cancelAll) {
            return this.keyWordFilter.cancelFilter(controlsState, { cancelAll: true });
        }

        if (
            this.numberFilter.isNumberFilterControlsState(controlsState) &&
            (config?.type === 'integer' || config?.type === 'float')
        ) {
            return this.numberFilter.cancelFilter(controlsState, null, config);
        }

        if (this.booleanFilter.isBooleanFilterControlsState(controlsState)) {
            return this.booleanFilter.cancelFilter(controlsState);
        }

        return controlsState;
    }

    public createRequestPayloadFromState(state: Filter_ControlsState): O.Option<Filter_RequestParams> {
        if (this.keyWordFilter.isKeywordFilterControlsState(state)) {
            return this.keyWordFilter.createRequestPayloadFromState(state);
        }

        if (this.numberFilter.isNumberFilterControlsState(state)) {
            return this.numberFilter.createRequestPayloadFromState(state);
        }

        if (this.booleanFilter.isBooleanFilterControlsState(state)) {
            return this.booleanFilter.createRequestPayloadFromState(state);
        }

        return O.none;
    }

    public createSnapshotFromState(controlsState: Filter_ControlsState): O.Option<Filter_Snapshot> {
        if (this.keyWordFilter.isKeywordFilterControlsState(controlsState)) {
            return this.keyWordFilter.createSnapshotFromState(controlsState);
        }

        if (this.numberFilter.isNumberFilterControlsState(controlsState)) {
            return this.numberFilter.createSnapshotFromState(controlsState);
        }

        if (this.booleanFilter.isBooleanFilterControlsState(controlsState)) {
            return this.booleanFilter.createSnapshotFromState(controlsState);
        }

        return O.none;
    }

    public applySnapshotToControlsState(
        controlsState: Filter_ControlsState,
        snapshot: Filter_Snapshot
    ): Filter_ControlsState {
        if (
            this.keyWordFilter.isKeywordFilterControlsState(controlsState) &&
            this.keyWordFilter.isKeywordFilterSnapshot(snapshot)
        ) {
            return this.keyWordFilter.applySnapshotToControlsState(controlsState, snapshot);
        }

        if (
            this.numberFilter.isNumberFilterControlsState(controlsState) &&
            this.numberFilter.isNumberFilterSnapshot(snapshot)
        ) {
            return this.numberFilter.applySnapshotToControlsState(controlsState, snapshot);
        }

        if (
            this.booleanFilter.isBooleanFilterControlsState(controlsState) &&
            this.booleanFilter.isBooleanFilterSnapshot(snapshot)
        ) {
            return this.booleanFilter.applySnapshotToControlsState(controlsState, snapshot);
        }

        return controlsState;
    }
}
